import React from "react";
import { View, StyleSheet, ViewStyle } from "react-native";
import { colors, radii } from "../../../styles/design-system";

type IconCircleSize = "xs" | "sm" | "md" | "lg" | "xl";
type IconCircleVariant =
  | "default"
  | "outlined"
  | "brand"
  | "brand-subtle"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "purple"
  | "teal"
  | "orange"
  | "purple-muted"
  | "teal-muted"
  | "orange-muted"
  | "success-muted"
  | "warning-muted"
  | "error-muted"
  | "info-muted";

type IconCircleRounded = "full" | "xl" | "lg" | "md";

interface IconCircleProps {
  /** The icon to render inside the circle */
  icon?: React.ReactNode;
  /** Children can be used instead of icon prop */
  children?: React.ReactNode;
  /** Size of the circle */
  size?: IconCircleSize;
  /** Color variant */
  variant?: IconCircleVariant;
  /** Border radius style */
  rounded?: IconCircleRounded;
  /** Additional styles */
  style?: ViewStyle;
}

const sizeStyles: Record<IconCircleSize, ViewStyle> = {
  xs: { width: 24, height: 24 },
  sm: { width: 32, height: 32 },
  md: { width: 40, height: 40 },
  lg: { width: 48, height: 48 },
  xl: { width: 64, height: 64 },
};

const roundedStyles: Record<IconCircleRounded, ViewStyle> = {
  full: { borderRadius: radii.full },
  xl: { borderRadius: radii.xl },
  lg: { borderRadius: radii.lg },
  md: { borderRadius: radii.md },
};

interface VariantColors {
  backgroundColor: string;
  iconColor: string;
}

const variantColors: Record<IconCircleVariant, VariantColors> = {
  default: {
    backgroundColor: colors.grey[100],
    iconColor: colors.grey[600],
  },
  outlined: {
    backgroundColor: colors.white,
    iconColor: colors.grey[400],
  },
  brand: {
    backgroundColor: colors.brand[50],
    iconColor: colors.brand[500],
  },
  "brand-subtle": {
    backgroundColor: colors.brand[50],
    iconColor: colors.brand[500],
  },
  success: {
    backgroundColor: colors.success[50],
    iconColor: colors.success[500],
  },
  warning: {
    backgroundColor: colors.warning[50],
    iconColor: colors.warning[500],
  },
  error: {
    backgroundColor: colors.error[50],
    iconColor: colors.error[500],
  },
  info: {
    backgroundColor: colors.blue[50],
    iconColor: colors.blue[500],
  },
  purple: {
    backgroundColor: colors.accent.purple50,
    iconColor: colors.accent.purple500,
  },
  teal: {
    backgroundColor: colors.accent.teal100,
    iconColor: colors.accent.teal500,
  },
  orange: {
    backgroundColor: colors.warning[50],
    iconColor: colors.warning[500],
  },
  "purple-muted": {
    backgroundColor: colors.accent.purple100,
    iconColor: colors.accent.purple500,
  },
  "teal-muted": {
    backgroundColor: colors.accent.teal100,
    iconColor: colors.accent.teal500,
  },
  "orange-muted": {
    backgroundColor: colors.warning[100],
    iconColor: colors.warning[500],
  },
  "success-muted": {
    backgroundColor: colors.success[100],
    iconColor: colors.success[500],
  },
  "warning-muted": {
    backgroundColor: colors.warning[100],
    iconColor: colors.warning[500],
  },
  "error-muted": {
    backgroundColor: colors.error[100],
    iconColor: colors.error[500],
  },
  "info-muted": {
    backgroundColor: colors.blue[100],
    iconColor: colors.blue[500],
  },
};

/**
 * IconCircle - A circular container for icons with semantic color variants.
 *
 * @example
 * <IconCircle icon={<Shield size={20} color={iconColor} />} variant="teal" size="lg" rounded="lg" />
 */
export function IconCircle({
  icon,
  children,
  size = "md",
  variant = "default",
  rounded = "full",
  style,
}: IconCircleProps) {
  const variantStyle = variantColors[variant];
  const outlinedStyle: ViewStyle = variant === "outlined" ? { borderWidth: 1, borderColor: colors.grey[200] } : {};

  return (
    <View
      style={[
        styles.container,
        sizeStyles[size],
        roundedStyles[rounded],
        { backgroundColor: variantStyle.backgroundColor },
        outlinedStyle,
        style,
      ]}>
      {icon || children}
    </View>
  );
}

// Helper to get icon color for a variant (useful when passing color to icon components)
export function getIconCircleColor(variant: IconCircleVariant = "default"): string {
  return variantColors[variant].iconColor;
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
});

export type { IconCircleProps, IconCircleSize, IconCircleVariant, IconCircleRounded };
